import PullToRefresh from "pulltorefreshjs";

declare global {
  interface Window {
    WMUPullToRefresh?: {
      init: () => void;
      refresh: (element: Element | string | null | undefined) => void;
      destroy: () => void;
    };
    __WMU_PULL_TO_REFRESH_BOOTED?: boolean;
  }
}

export type WMUPullToRefreshAPI = {
  init: () => void;
  refresh: (element: Element | string | null | undefined) => void;
  destroy: () => void;
};

const doc = document;
const PTR_SELECTOR = "[data-pull-to-refresh]";
const CLASS_PREFIX = "wmu-ptr--";
const MOBILE_VIEWPORT = "(hover: none) and (pointer: coarse), (max-width: 767.98px)";

function getRootElement(element: HTMLElement): HTMLElement {
  const selector = String(element.dataset.ptrRoot || "").trim();
  if (selector) {
    const match = doc.querySelector(selector);
    if (match instanceof HTMLElement) {
      return match;
    }
  }
  return element;
}

function isEligibleViewport(): boolean {
  return window.matchMedia(MOBILE_VIEWPORT).matches;
}

function shouldPull(root: HTMLElement): boolean {
  if (doc.body.classList.contains("wm-sheet-open")) {
    return false;
  }
  const modalOpen = doc.body.classList.contains("swal2-shown") || Boolean(doc.querySelector(".swal2-container"));
  if (modalOpen) {
    return false;
  }
  if (root.scrollTop > 0) {
    return false;
  }
  return window.scrollY <= 0;
}

function getInstruction(element: HTMLElement, key: "ptrPull" | "ptrRelease" | "ptrRefreshing", fallback: string): string {
  const raw = element.dataset[key];
  const value = typeof raw === "string" ? raw.trim() : "";
  return value || fallback;
}

function refreshElement(element: HTMLElement): Promise<unknown> | void {
  const url = String(element.dataset.ptrUrl || "").trim();
  const target = String(element.dataset.ptrTarget || "").trim();
  const swap = String(element.dataset.ptrSwap || "").trim() || "innerHTML";
  const mode = String(element.dataset.ptrMode || "").trim().toLowerCase();

  if ((mode === "reload" || (!url && !target)) && typeof window.location.reload === "function") {
    window.location.reload();
    return;
  }

  if (url && window.htmx) {
    const targetOption: string | Element | undefined = target || undefined;
    const request = window.htmx.ajax("GET", url, {
      source: element,
      target: targetOption,
      swap,
    });
    return request instanceof Promise ? request : Promise.resolve(request);
  }

  if (url) {
    return window.fetch(url, {
      credentials: "same-origin",
      headers: { Accept: "text/html" },
    }).then(async (response) => {
      if (!response.ok) {
        throw new Error(`pull-to-refresh request failed: ${response.status}`);
      }
      if (!target) {
        window.location.reload();
        return;
      }
      const targetNode = doc.querySelector(target);
      if (!(targetNode instanceof HTMLElement)) {
        throw new Error(`pull-to-refresh target not found: ${target}`);
      }
      targetNode.innerHTML = await response.text();
      window.htmx?.process?.(targetNode);
    });
  }

  window.location.reload();
}

function initAll(): void {
  PullToRefresh.destroyAll();

  if (!isEligibleViewport()) {
    return;
  }

  doc.querySelectorAll(PTR_SELECTOR).forEach((node) => {
    if (!(node instanceof HTMLElement)) {
      return;
    }

    const root = getRootElement(node);
    const threshold = Number.parseInt(String(node.dataset.ptrThreshold || ""), 10);

    PullToRefresh.init({
      mainElement: root,
      triggerElement: root,
      classPrefix: CLASS_PREFIX,
      distThreshold: Number.isFinite(threshold) ? threshold : 72,
      distMax: 96,
      distReload: 56,
      instructionsPullToRefresh: getInstruction(node, "ptrPull", "Pull to refresh"),
      instructionsReleaseToRefresh: getInstruction(node, "ptrRelease", "Release to sync"),
      instructionsRefreshing: getInstruction(node, "ptrRefreshing", "Refreshing..."),
      iconArrow: "↓",
      iconRefreshing: "⟳",
      refreshTimeout: 0,
      getMarkup: () => `
        <div class="${CLASS_PREFIX}box pull-refresh-indicator-shell">
          <div class="pull-refresh-indicator">
            <span class="${CLASS_PREFIX}icon pull-refresh-icon" aria-hidden="true"></span>
            <span class="${CLASS_PREFIX}text pull-refresh-text"></span>
          </div>
        </div>
      `,
      getStyles: () => "",
      shouldPullToRefresh: () => shouldPull(root),
      onRefresh: () => refreshElement(node),
    });
  });
}

function refresh(element: Element | string | null | undefined): void {
  if (typeof element === "string") {
    const found = doc.querySelector(element);
    if (found instanceof HTMLElement) {
      void refreshElement(found);
    }
    return;
  }
  if (element instanceof HTMLElement) {
    void refreshElement(element);
  }
}

function boot(): void {
  if (window.__WMU_PULL_TO_REFRESH_BOOTED) {
    initAll();
    return;
  }
  window.__WMU_PULL_TO_REFRESH_BOOTED = true;
  window.WMUPullToRefresh = {
    init: initAll,
    refresh,
    destroy: () => PullToRefresh.destroyAll(),
  } satisfies WMUPullToRefreshAPI;

  initAll();

  doc.body.addEventListener("htmx:afterSwap", () => {
    initAll();
  });
}

boot();
